import React, {useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {DataTable} from "primereact/datatable";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {OverlayPanel} from "primereact/overlaypanel";
import {Tag} from "primereact/tag";
import Avatar from "../../../system/components/avatar";
import Can from "../../../system/can";
import {setMessage} from "../../../system/app-slice";
import {useTranslate} from "../../../system/useTranslate";
import {useTeam} from "../../../models/team/use-team";
import {usePermission} from "../../../system/usePermission";
import PageHeaderButton from "../../components/page-header-button";
import MultiSelectMembers from "../../tasks/components/multi-select-members";
import {prepareTeamMemberRows, teamMemberProfile} from "../team-members";

function Members({teamData, onChange}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const teamApi = useTeam();
    const permission = usePermission();
    const picker = useRef();
    const [searchText, setSearchText] = useState("");
    const [pendingId, setPendingId] = useState(null);
    const rows = prepareTeamMemberRows(teamData?.members, searchText, teamData?.owner);
    const selectedMemberships = prepareTeamMemberRows(teamData?.members, "", teamData?.owner);

    const openProfile = (row) => {
        const profile = teamMemberProfile(row);
        const id = profile?.documentId || profile?.id;
        if (id) navigate(`/user-profile/${id}`);
    };

    const updateMember = async (change) => {
        const membership = change?.member;
        const identifier = membership?.documentId || membership?.id;
        if (!identifier) return;
        setPendingId(String(identifier));
        try {
            await teamApi.updateTeamMember(teamData.documentId, membership, change.status === "removed" ? "remove" : "add");
            dispatch(setMessage({severity: "success", content: translate("team.members.updated")}));
            await onChange?.();
        } catch (error) {
            dispatch(setMessage({severity: "error", content: translate(error?.message || "team.members.update-error")}));
        } finally {
            setPendingId(null);
        }
    };

    const requestRemove = (row) => {
        if (row.isOwner) return;
        confirmDialog({
            header: translate("team.members.remove-title"),
            message: translate("team.members.remove-message").replace("{{NAME}}", row.fullname),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: translate("remove"),
            rejectLabel: translate("cancel"),
            acceptClassName: "p-button-danger text-xs",
            rejectClassName: "p-button-text text-xs",
            accept: () => updateMember({status: "removed", member: row}),
        });
    };

    const memberBody = (row) => <div role="button" tabIndex={0} className="flex align-items-center gap-2 min-w-0 cursor-pointer"
        onClick={() => openProfile(row)} onKeyDown={(event) => ["Enter", " "].includes(event.key) && openProfile(row)}>
        <Avatar memberData={row.member}/>
        <div className="flex flex-column min-w-0">
            <strong className="text-xs text-900 text-overflow-ellipsis overflow-hidden">{row.fullname}</strong>
            <small className="text-xs text-500">{row.email || translate("team.member.email-empty")}</small>
        </div>
    </div>;

    const roleBody = (row) => <Tag severity={row.isOwner ? "warning" : "info"} value={translate(row.isOwner ? "team.role.owner" : row.roleKey)}/>;
    const statusBody = (row) => {
        const active = row.statusKey === "team.member.status.joined";
        return <span className={`inline-flex align-items-center gap-2 white-space-nowrap ${active ? "text-green-600" : "text-500"}`}>
            <span className={`block border-circle p-1 ${active ? "bg-green-500" : "bg-gray-300"}`}/>
            {translate(row.statusKey)}
        </span>;
    };
    const actionBody = (row) => <div className="flex align-items-center gap-1">
        <Button text rounded className="w-2rem h-2rem" icon="pi pi-eye" aria-label={translate("team.members.view-profile")}
            onClick={() => openProfile(row)}/>
        <Can permission="manage-teams"><Button text rounded severity="danger" className="w-2rem h-2rem" icon="pi pi-trash"
            aria-label={translate("team.members.remove")} disabled={row.isOwner || pendingId === String(row.documentId || row.id)}
            loading={pendingId === String(row.documentId || row.id)} onClick={() => requestRemove(row)}/></Can>
    </div>;
    const canInvite = permission.can("manage-teams") || teamData?.accessConfigs?.invite === "all-members";

    return <div className="h-full flex flex-column p-3 gap-4 overflow-auto">
        <ConfirmDialog className="rtl"/>
        <PageHeaderButton title={translate("team-members")} description={translate("team-members-description")} end={<div className="flex flex-column md:flex-row gap-2">
            <IconField dir="rtl">
                <InputIcon className="pi pi-search"/>
                <InputText className="w-full" placeholder={translate("search-members-placeholder")} value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}/>
            </IconField>
            {canInvite && <Button icon="pi pi-user-plus" label={translate("invite-member")} className="p-button-sm text-xs gap-2"
                onClick={(event) => picker.current.toggle(event)}/>}
            <OverlayPanel pt={{root: () => ({className: "p-0 rtl"}), content: () => ({className: "p-0"})}} ref={picker}>
                <MultiSelectMembers onChange={updateMember} value={selectedMemberships}/>
            </OverlayPanel>
        </div>}/>
        <section className="surface-card border-1 border-100 border-round-2xl overflow-hidden">
        <DataTable value={rows} dataKey="documentId" paginator={rows.length > 10} rows={10} rowsPerPageOptions={[10, 20, 50]}
            emptyMessage={translate(searchText ? "team.members.search-empty" : "team.members.empty")}
            className="text-xs" scrollable tableStyle={{minWidth: "48rem"}} dir="rtl"
            pt={{
                headerRow: {className: "surface-0"},
                headerCell: {className: "surface-0 text-700 font-normal text-xs text-right py-3 px-3 border-bottom-1 border-100"},
                bodyRow: {className: "surface-0 hover:surface-50"},
                bodyCell: {className: "text-xs text-right py-3 px-3 border-bottom-1 border-100"},
            }}>
            <Column className="text-right" header="#" body={(_, options) => options.rowIndex + 1} style={{width: "3.5rem"}}/>
            <Column className="text-right" header={translate("member")} body={memberBody} sortable sortField="fullname" style={{minWidth: "15rem"}}/>
            <Column className="text-right" header={translate("role")} body={roleBody} sortable sortField="isOwner" style={{minWidth: "9rem"}}/>
            <Column className="text-right" header={translate("status")} body={statusBody} sortable sortField="memberShipStatus" style={{minWidth: "8rem"}}/>
            <Column className="text-right" header={translate("operations")} body={actionBody} style={{width: "8rem"}}/>
        </DataTable>
        </section>
    </div>;
}

export default Members;
